import React, { useCallback, useEffect, useLayoutEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import App from "./App";
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  localeToPathPrefix,
  normalizeLocaleFromPathname,
  stripLocalePrefix,
} from "./i18n/locales";
import { loadLocaleFonts } from "./i18n/loadLocaleFonts";
import { HREFLANG_ALTERNATES, SITE_ORIGIN } from "./i18n/hreflang";
import {
  readPreferredLocaleForRedirect,
  saveLocalePreference,
} from "./i18n/localePreference";

function useMatomoPageViews() {
  const location = useLocation();

  useEffect(() => {
    // Matomo SPA tracking (best-effort; noop if Matomo not loaded).
    if (!window._paq) return;
    window._paq.push(["setCustomUrl", location.pathname + location.search + location.hash]);
    window._paq.push(["trackPageView"]);
  }, [location.pathname, location.search, location.hash]);
}

function LocaleHead({ locale }) {
  const { t } = useTranslation();
  const prefix = localeToPathPrefix(locale);
  const canonical = `${SITE_ORIGIN}${prefix || "/"}`;

  return (
    <Helmet
      htmlAttributes={{ lang: locale }}
      bodyAttributes={{ lang: locale }}
      defaultTitle={t("meta.title", { lng: locale })}
    >
      <title>{t("meta.title", { lng: locale })}</title>
      <link rel="canonical" href={canonical} />
      {HREFLANG_ALTERNATES.map((a) => (
        <link key={a.lang} rel="alternate" hrefLang={a.lang} href={a.href} />
      ))}
    </Helmet>
  );
}

function LocalizedApp() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locale = normalizeLocaleFromPathname(location.pathname);

  const setLocale = useCallback(
    (nextLocale) => {
      const desired = isSupportedLocale(nextLocale) ? nextLocale : DEFAULT_LOCALE;
      saveLocalePreference(desired, { userChoice: true });

      const basePath = stripLocalePrefix(location.pathname);
      const prefix = localeToPathPrefix(desired);
      const nextPath = `${prefix}${basePath === "/" ? "/" : basePath}`;

      navigate(`${nextPath}${location.search}${location.hash}`, { replace: true });
    },
    [location.hash, location.pathname, location.search, navigate]
  );

  useLayoutEffect(() => {
    // Keep global i18n in sync (e.g. hooks without explicit lng); UI uses locale prop.
    const current = (i18n.resolvedLanguage || i18n.language || "").split("-")[0];
    if (current !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [i18n, locale]);

  useEffect(() => {
    // Remember locale from URL (/ja/, etc.) without treating it as a manual footer choice.
    if (locale !== DEFAULT_LOCALE) {
      saveLocalePreference(locale);
    }
  }, [locale]);

  useEffect(() => {
    loadLocaleFonts(locale);
  }, [locale]);

  return (
    <>
      <LocaleHead locale={locale} />
      <App key={locale} locale={locale} setLocale={setLocale} />
    </>
  );
}

function LocaleGate() {
  const { pathname } = useLocation();
  const segment = pathname.split("/")[1];

  if (segment && !isSupportedLocale(segment)) {
    return <Navigate to="/" replace />;
  }

  return <LocalizedApp />;
}

/** Redirect "/" to browser/saved locale without mounting the English app first. */
function RootRoute() {
  const location = useLocation();
  const preferred = readPreferredLocaleForRedirect();

  if (preferred !== DEFAULT_LOCALE) {
    return (
      <Navigate
        to={`/${preferred}/${location.search}${location.hash}`}
        replace
      />
    );
  }

  return <LocalizedApp />;
}

export default function AppRouter() {
  useMatomoPageViews();

  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/:lang/*" element={<LocaleGate />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

